import React, { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Cell,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  LabelList,
  Treemap,
  Area,
  AreaChart,
} from 'recharts';
import { Gauge, Users, LineChart, Truck, Clock, RefreshCw } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Switch } from '../../../components/ui/Switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/index.js';
import useAuth from 'hooks/useAuth';
import useTheme from 'hooks/useTheme';
import dayjs from 'dayjs';
import './ModernDashboardAdmin.scss';
import ClientVolumeTab from './tabs/ClientVolumeTab';
import WeeklyComparisonTab from './tabs/WeeklyComparisonTab';
import DriverPerformanceTab from './tabs/DriverPerformanceTab';
import PendingBreakdownTab from './tabs/PendingBreakdownTab';
import { dashboardService } from '../../../services/dashboardService';
import { getOperationTimezone } from '../../../utils/timezone';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { orderTypeLabel } from '../../../utils/orderTypeLabel';
import {
  ChartSkeleton,
  KpiCard,
  LastUpdatedBadge,
  ErrorState,
  EmptyState,
  COLORS,
  CHART_PALETTE,
} from '../../../components/dashboard/shared/DashboardShared';


const TAB_KEYS = [
  { id: '1', labelKey: 'Operativo', Icon: Gauge },
  { id: '2', labelKey: 'Volumen por Cliente', Icon: Users },
  { id: '3', labelKey: 'Comparativa Semanal', Icon: LineChart },
  { id: '4', labelKey: 'Performance Repartidores', Icon: Truck },
  { id: '5', labelKey: 'Pendientes', Icon: Clock },
];

const TOP_N = 15;

// Auto-refresh del tab Operativo: tick de 60s, con opt-out clickeable desde el
// header que persiste por usuario en localStorage.
const AUTO_REFRESH_MS = 60000;
const AUTO_REFRESH_STORAGE_KEY = 'dashboard.autoRefresh60';

const capitalizeWords = (str, fallback = i18n.t('Sin datos')) => {
  if (!str) return fallback;
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
};

const truncateLabel = (str, maxLen = 18) => {
  const s = capitalizeWords(str);
  return s.length > maxLen ? s.substring(0, maxLen) + '\u2026' : s;
};

const slColorByPct = (pct) => {
  if (pct >= 90) return COLORS.success;
  if (pct >= 80) return COLORS.primary;
  if (pct >= 70) return COLORS.warning;
  return COLORS.danger;
};

// ── CommuneRouteTable ─────────────────────────────────────────────────────────

const CommuneRouteRow = ({ row, selectedDateDisplay, tickColor, refreshing }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const nsCol = row.ns ? slColorByPct(row.ns.pct) : tickColor;
  const hasDrivers = row.drivers && row.drivers.length > 0;

  return (
    <>
      <div
        // La clase --clickable es la que pinta el hover: sin repartidores la fila
        // no tiene acción, así que no debe ofrecer el clic (era clic muerto).
        className={`commune-unified-table__row${hasDrivers ? ' commune-unified-table__row--clickable' : ''}${open ? ' commune-unified-table__row--open' : ''}`}
        onClick={() => hasDrivers && setOpen((v) => !v)}
        style={{ cursor: hasDrivers ? 'pointer' : 'default' }}
        title={hasDrivers ? t('Clic para ver repartidores') : undefined}
      >
        <span
          className="commune-unified-table__col-name"
          style={{ borderLeftColor: row.ns ? nsCol : 'transparent' }}
          title={row.name}
        >
          {hasDrivers && (
            <i
              className={`fas fa-chevron-${open ? 'down' : 'right'} commune-unified-table__chevron`}
            />
          )}
          {row.name}
        </span>
        <span className="commune-unified-table__col-drivers">
          {row.driverCount > 0 ? (
            <span style={{ color: COLORS.primary, fontWeight: 600 }}>
              <i className="fas fa-user commune-unified-table__icon-sm" />
              {row.driverCount}
            </span>
          ) : '—'}
        </span>
        <span className="commune-unified-table__col-avg">
          {row.avgPkgsPerDriver > 0 ? (
            <span style={{ color: COLORS.secondary }}>
              {row.avgPkgsPerDriver.toFixed(1)}
            </span>
          ) : '—'}
        </span>
        <span className="commune-unified-table__col-ns">
          {row.ns ? (
            <span style={{ color: nsCol, fontWeight: 600 }}>
              <span className="commune-unified-table__dot" style={{ background: nsCol }} />
              {row.ns.pct}%
            </span>
          ) : (
            <span style={{ color: tickColor }}>—</span>
          )}
        </span>
        <span className="commune-unified-table__col-count" style={{ color: COLORS.warning }}>
          {row.routeCount > 0 ? row.routeCount : '—'}
        </span>
      </div>
      {hasDrivers && open && (
        <div className="commune-unified-table__drivers">
          {row.drivers.map((driver, idx) => (
            <div key={driver.driverId || idx} className="commune-unified-table__driver-row">
              <span className="commune-unified-table__driver-name">
                <i className="fas fa-user-circle commune-unified-table__icon-sm" />
                {driver.name}
              </span>
              <span className="commune-unified-table__driver-count" style={{ color: COLORS.warning }}>
                {driver.packageCount} {t('paq.')}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const CommuneRouteTable = ({ rows, loading, selectedDateDisplay, tickColor, refreshing }) => {
  const { t } = useTranslation();
  return (
  <div className={`chart-card chart-card--full-height${refreshing ? ' chart-card--refreshing' : ''}`}>
    <div className="chart-card__header">
      <h4 className="chart-card__title">
        {t('Comunas — Repartidores en Ruta & Nivel de Servicio')}
        <span
          className="dash-info-tip"
          data-tip={t('Top {{n}} comunas con paquetes en ruta hoy. Muestra repartidores activos, promedio de paquetes por repartidor y NS del {{date}} (% entregados antes de 21:00). Clic en una fila para ver el detalle de repartidores.', { n: rows.length, date: selectedDateDisplay })}
        >
          <i className="fas fa-info-circle" />
        </span>
      </h4>
    </div>
    <div className="card-body">
      {loading ? (
        <ChartSkeleton height={300} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="fas fa-map-marker-alt"
          title={t('Sin datos de comunas')}
          subtitle={t('No hay paquetes en ruta ni entregas registradas para esta fecha.')}
        />
      ) : (
        <div className="commune-unified-table">
          <div className="commune-unified-table__header">
            <span>{t('Comuna')}</span>
            <span>{t('Repartidores')}</span>
            <span>{t('Paq/Rep')}</span>
            <span>{`NS ${selectedDateDisplay}`}</span>
            <span>{t('En ruta')}</span>
          </div>
          {rows.map((row) => (
            <CommuneRouteRow
              key={row.name}
              row={row}
              selectedDateDisplay={selectedDateDisplay}
              tickColor={tickColor}
              refreshing={refreshing}
            />
          ))}
          <div className="commune-unified-table__legend">
            <span><span className="commune-unified-table__dot" style={{ background: COLORS.success }} />NS ≥ 90%</span>
            <span><span className="commune-unified-table__dot" style={{ background: COLORS.primary }} />NS ≥ 80%</span>
            <span><span className="commune-unified-table__dot" style={{ background: COLORS.warning }} />NS ≥ 70%</span>
            <span><span className="commune-unified-table__dot" style={{ background: COLORS.danger }} />NS &lt; 70% ⚠</span>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

// ── Custom tooltips (defined outside to avoid recreation on each render) ─────

const MunicipalityCountTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="cv-tooltip">
      <div className="cv-tooltip__label">{d.fullName}</div>
      <div className="cv-tooltip__row">
        <span className="cv-tooltip__dot" style={{ background: payload[0]?.fill }} />
        <span><strong>{payload[0]?.value}</strong> {t('paquetes')}</span>
      </div>
    </div>
  );
};

// ── ModernChart / SectionHeader ──────────────────────────────────────────────
// A nivel de módulo A PROPÓSITO: definidos dentro del componente eran un tipo
// nuevo en cada render, y la reconciliación de React desmontaba y remontaba el
// subtree completo de cada chart (ResponsiveContainer incluido, con re-medición
// y re-animación de recharts) en CADA re-render del dashboard — el "se recarga
// solo" del treemap de Distribución por Cliente.

const ModernChart = ({ title, subtitle, children, height = 300, loading, refreshing }) => (
  <div className={`chart-card chart-card--full-height${refreshing ? ' chart-card--refreshing' : ''}`}>
    <div className="chart-card__header">
      <h4 className="chart-card__title">
        {title}
        {subtitle && (
          <span className="dash-info-tip" data-tip={subtitle}>
            <i className="fas fa-info-circle" />
          </span>
        )}
      </h4>
    </div>
    <div className="card-body" style={{ minHeight: height + 32 }}>
      {loading ? (
        <ChartSkeleton height={height} />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  </div>
);

// Section divider with title and description
const SectionHeader = ({ title, description }) => (
  <div className="dashboard-section-header mb-2">
    <div className="dashboard-section-header__content">
      <h5 className="dashboard-section-header__title">{title}</h5>
      {description && <span className="dashboard-section-header__desc">{description}</span>}
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const ModernDashboardAdmin = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [operationalData, setOperationalData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isInitialLoad = useRef(true);
  // Espejo en ref para que el interval de auto-refresh (que no se recrea al
  // cambiar de tab) sepa cuál es el tab activo.
  const activeTabRef = useRef(activeTab);
  // Auto-refresh encendido por defecto; la elección del usuario persiste.
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try {
      return localStorage.getItem(AUTO_REFRESH_STORAGE_KEY) !== '0';
    } catch {
      return true;
    }
  });

  const { user } = useAuth();
  const { isDark } = useTheme();
  const logisticCompanyId = useMemo(() => user?.logisticCompany?._id || null, [user]);
  const dashboardIanaTz = useMemo(() => getOperationTimezone(user), [user]);

  // Colores de ticks/grilla/tooltip de recharts derivados de los tokens del
  // design-system (theme-variables: hex semánticos que conmutan con .dark).
  // recharts pinta atributos SVG, así que leemos el valor concreto en vivo.
  // isDark fuerza el recálculo al cambiar de tema.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { tickColor, gridColor, tooltipStyle, tooltipItemStyle } = useMemo(() => {
    const v = (name, fallback) => {
      if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
      const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return val || fallback;
    };
    return {
      tickColor: v('--text-secondary', '#6B7280'),
      gridColor: v('--border-primary', '#E5E7EB'),
      tooltipStyle: {
        backgroundColor: v('--surface-primary', '#FFFFFF'),
        border: `1px solid ${v('--border-primary', '#E5E7EB')}`,
        color: v('--text-primary', '#171717'),
      },
      tooltipItemStyle: { color: v('--text-primary', '#171717') },
    };
  }, [isDark]);

  const handleDateChange = (e) => {
    // A cleared date input would reach dayjs as '' → 'Invalid Date' → a 400.
    if (!e.target.value) return;
    isInitialLoad.current = true;
    setInitialLoading(true);
    setLoadError(null);
    setSelectedDate(dayjs(e.target.value).format('YYYY-MM-DD'));
  };

  const fetchAllData = useCallback(async (isManualRefresh = false) => {
    if (!logisticCompanyId) return;

    const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
    const firstLoad = isInitialLoad.current;

    if (isManualRefresh || !firstLoad) setIsRefreshing(true);
    if (isManualRefresh) setLoadError(null);

    try {
      const response = await dashboardService.getOperationalSummary(
        logisticCompanyId,
        formattedDate,
        dashboardIanaTz,
      );
      const data = response.data;

      const processedHalfHour = Object.entries(data.deliveriesByHalfHour || {})
        .filter(([timestamp, count]) => {
          const time = dayjs(timestamp).format('HH:mm');
          const date = dayjs(timestamp).format('YYYY-MM-DD');
          return count > 0 && time >= '08:00' && time <= '23:59' && date === formattedDate;
        })
        .map(([timestamp, count]) => ({ time: dayjs(timestamp).format('HH:mm'), count }));

      setOperationalData({ ...data, deliveriesByHalfHourProcessed: processedHalfHour });
      setLastUpdated(new Date());
      setLoadError(null);
    } catch (err) {
      // Object, not the string itself: apiErrorMessage() can return null and the
      // state doubles as the "there is an error" flag.
      setLoadError({ message: apiErrorMessage(err) });
    } finally {
      if (firstLoad) {
        isInitialLoad.current = false;
        setInitialLoading(false);
      }
      setIsRefreshing(false);
    }
  }, [logisticCompanyId, selectedDate, dashboardIanaTz]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const handleAutoRefreshChange = (next) => {
    setAutoRefresh(next);
    try {
      localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* storage bloqueado: la elección queda solo en memoria */
    }
    // Al encender con el dato ya viejo, refresca de inmediato en vez de
    // esperar hasta 60s el próximo tick. En el handler y NO en el efecto del
    // interval: un fetch ahí se duplicaría con el del efecto de datos en cada
    // cambio de fecha.
    if (next && (!lastUpdated || Date.now() - lastUpdated.getTime() > AUTO_REFRESH_MS)) {
      fetchAllData(false);
    }
  };

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    fetchAllData();
  }, [logisticCompanyId, selectedDate, fetchAllData]);

  // Tick de auto-refresh, solo con el toggle encendido. operationalData solo
  // se pinta en el tab Operativo ('1'): con otro tab activo el contenido está
  // desmontado, así que el tick no fetchea.
  useEffect(() => {
    if (!logisticCompanyId || !autoRefresh) return undefined;
    const interval = setInterval(() => {
      if (!document.hidden && activeTabRef.current === '1') fetchAllData(false);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [logisticCompanyId, autoRefresh, fetchAllData]);

  // Al VOLVER al tab Operativo (transición real de tab — no cambios de fecha,
  // que ya cubre el efecto de datos), refresca de inmediato: el auto-refresh
  // estuvo gateado mientras se miraban los otros tabs. Incondicional a
  // propósito (incluso con el toggle apagado): cubre la carga inicial fallida
  // y el contenido recién remontado; operational-summary es chico y está
  // cacheado en Redis.
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    const prev = prevTabRef.current;
    prevTabRef.current = activeTab;
    if (activeTab !== '1' || prev === '1') return;
    fetchAllData(false);
  }, [activeTab, fetchAllData]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const { delivered, total, roundedPercentage } = useMemo(() => {
    const d = operationalData?.packagesPercentage;
    return {
      delivered: d?.delivered || 0,
      total: d?.total || 0,
      roundedPercentage: Math.round((d?.percentage || 0) * 10) / 10,
    };
  }, [operationalData]);

  const averageDeliveryTimeFormatted = useMemo(() => {
    const rawMs = operationalData?.averageDeliveryTime?.averageDeliveryTime || 0;
    const minutes = rawMs > 0 ? Math.round(rawMs / 60000) : 0;
    return `${Math.floor(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
  }, [operationalData]);

  const treemapData = useMemo(
    () => (operationalData?.packagesCollected?.senders || [])
      .sort((a, b) => b.count - a.count)
      .map((item) => ({ name: item.businessName, value: item.count })),
    [operationalData]
  );

  const totalCollectedPackages = useMemo(
    () => operationalData?.packagesCollected?.total || 0,
    [operationalData]
  );

  const deliveriesByHalfHourData = useMemo(
    () => operationalData?.deliveriesByHalfHourProcessed || [],
    [operationalData]
  );

  const maxDeliveries = useMemo(
    () => Math.max(...deliveriesByHalfHourData.map((item) => item.count), 1),
    [deliveriesByHalfHourData]
  );

  const serviceLevelGlobal = useMemo(() => ({
    total: operationalData?.serviceLevel?.total || 0,
    deliveredBeforeCutoff: operationalData?.serviceLevel?.deliveredBeforeCutoff || 0,
    serviceLevelPct: operationalData?.serviceLevel?.serviceLevelPct || 0,
  }), [operationalData]);

  const sameDayAttention = useMemo(() => ({
    totalCollected: operationalData?.sameDayAttention?.totalCollected || 0,
    sameDayBeforeCutoff: operationalData?.sameDayAttention?.sameDayBeforeCutoff || 0,
    attentionPct: operationalData?.sameDayAttention?.attentionPct || 0,
  }), [operationalData]);

  const selectedDateDisplay = useMemo(
    () => dayjs(selectedDate).format('DD/MM/YYYY'),
    [selectedDate],
  );

  const serviceLevelByTypeMapped = useMemo(() =>
    (operationalData?.serviceLevelByType || []).map((item) => ({
      name: orderTypeLabel(item.type),
      pct: item.serviceLevelPct,
      total: item.total,
      before: item.deliveredBeforeCutoff,
    })),
    [operationalData]
  );

  const collectedByMunicipalityTop = useMemo(() =>
    [...(operationalData?.collectedByMunicipality || [])]
      .slice(0, TOP_N)
      .map((item) => ({
        name: truncateLabel(item.municipality),
        fullName: capitalizeWords(item.municipality),
        count: item.count,
      }))
      .reverse(),
    [operationalData]
  );

  // Join de NS por comunas + Paquetes en ruta por comunas en un único dataset
  const communeUnifiedData = useMemo(() => {
    const nsMap = new Map(
      (operationalData?.serviceLevelByMunicipality || []).map((item) => [
        (item.municipality || '').toLowerCase().trim(),
        { pct: item.serviceLevelPct, total: item.total, before: item.deliveredBeforeCutoff },
      ])
    );
    const routeMap = new Map(
      (operationalData?.inRouteByMunicipality || []).map((item) => [
        (item.municipality || '').toLowerCase().trim(),
        {
          count: item.count,
          driverCount: item.driverCount ?? 0,
          avgPkgsPerDriver: item.avgPkgsPerDriver ?? 0,
          drivers: item.drivers ?? [],
        },
      ])
    );
    const allKeys = new Set([...nsMap.keys(), ...routeMap.keys()]);
    const rows = [...allKeys]
      .map((key) => {
        const route = routeMap.get(key);
        return {
          name: capitalizeWords(key),
          routeCount: route?.count ?? 0,
          driverCount: route?.driverCount ?? 0,
          avgPkgsPerDriver: route?.avgPkgsPerDriver ?? 0,
          drivers: route?.drivers ?? [],
          ns: nsMap.get(key) ?? null,
        };
      })
      .filter((d) => d.routeCount > 0 || d.ns !== null)
      .sort((a, b) => b.routeCount - a.routeCount)
      .slice(0, TOP_N);
    return { rows };
  }, [operationalData]);

  const inRouteByTypeMapped = useMemo(() =>
    (operationalData?.inRouteByType || []).map((item) => ({ name: orderTypeLabel(item.type), count: item.count })),
    [operationalData]
  );

  // Dynamic height for horizontal bar charts
  const hBarHeight = (dataLen) => Math.max(220, dataLen * 30 + 40);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full min-w-0 modern-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header__content">
          <h1 className="dashboard-header__title">{t('Dashboard de Performance')}</h1>
          <p className="dashboard-header__subtitle">
            {t('Nivel de servicio, operación en tiempo real y distribución de entregas')}
          </p>
        </div>
        {activeTab === '1' && (
          <div className="dashboard-header__controls">
            <label htmlFor="date" className="dashboard-header__label">{t('Fecha')}:</label>
            <div className="dashboard-header__controls-row">
              <Input
                type="date"
                id="date"
                size="sm"
                value={selectedDate}
                onChange={handleDateChange}
                className="w-auto"
              />
              <button
                type="button"
                className="dashboard-header__refresh-btn"
                onClick={() => fetchAllData(true)}
                disabled={isRefreshing || initialLoading}
                title={t('Actualizar datos')}
                aria-label={t('Actualizar datos')}
              >
                <RefreshCw size={16} className={isRefreshing ? 'spinning' : ''} aria-hidden />
              </button>
              <Switch
                size="sm"
                checked={autoRefresh}
                onChange={handleAutoRefreshChange}
                ariaLabel={t('Actualizar automáticamente cada 60 segundos')}
                label={
                  <span
                    className="text-xs font-semibold text-fg-muted whitespace-nowrap"
                    title={t('Actualizar automáticamente cada 60 segundos')}
                  >
                    {t('Auto')} {AUTO_REFRESH_MS / 1000}s
                  </span>
                }
              />
            </div>
            <LastUpdatedBadge timestamp={lastUpdated} isRefreshing={isRefreshing} />
            {isRefreshing && <div className="dashboard-header__progress" />}
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-3" ariaLabel={t('Secciones del dashboard')}>
          {TAB_KEYS.map(({ id, labelKey, Icon }) => (
            <TabsTrigger key={id} value={id} icon={<Icon />}>
              {t(labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Tab 1: Operativo ────────────────────────────────────────────── */}
        <TabsContent value="1">

          {/* Error state */}
          {loadError && !initialLoading && (
            <div className="mb-3">
              <ErrorState message={loadError.message} onRetry={() => fetchAllData(true)} />
            </div>
          )}

          {/* ROW 1: KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
            <KpiCard
              title={t('Cumplimiento')}
              value={`${roundedPercentage}%`}
              tooltip={`${delivered} ${t('entregados de')} ${total} ${t('recolectados hoy')}`}
              icon="fas fa-check-circle"
              color={COLORS.success}
              loading={initialLoading}
              isRefreshing={isRefreshing}
            />
            <KpiCard
              title={t('Atención mismo día')}
              value={`${Math.round(sameDayAttention.attentionPct * 10) / 10}%`}
              tooltip={`${sameDayAttention.sameDayBeforeCutoff} ${t('de')} ${sameDayAttention.totalCollected} ${t('recolectados el')} ${selectedDateDisplay}, ${t('entregados el mismo día antes de 21:00 (excluye cancelados y pedidos solo creados)')}`}
              icon="fas fa-bolt"
              color={slColorByPct(sameDayAttention.attentionPct)}
              loading={initialLoading}
              isRefreshing={isRefreshing}
            />
            <KpiCard
              title={t('Tiempo Promedio')}
              value={averageDeliveryTimeFormatted}
              tooltip={t('Tiempo promedio desde recolección hasta entrega efectiva')}
              icon="fas fa-clock"
              color={COLORS.primary}
              loading={initialLoading}
              isRefreshing={isRefreshing}
            />
            <KpiCard
              title={t('Paquetes Recolectados')}
              value={totalCollectedPackages.toLocaleString()}
              tooltip={`${t('Total recolectados el')} ${selectedDateDisplay} ${t('(calendario local)')}`}
              icon="fas fa-box"
              color={COLORS.info}
              loading={initialLoading}
              isRefreshing={isRefreshing}
            />
            <div className="sm:col-span-2 lg:col-span-1">
              <KpiCard
                title={t('Nivel de Servicio Global')}
                value={`${serviceLevelGlobal.serviceLevelPct}%`}
                tooltip={`${serviceLevelGlobal.deliveredBeforeCutoff} ${t('de')} ${serviceLevelGlobal.total} ${t('entregados el')} ${selectedDateDisplay} ${t('antes de 21:00 hs.')}`}
                icon="fas fa-trophy"
                color={slColorByPct(serviceLevelGlobal.serviceLevelPct)}
                loading={initialLoading}
                isRefreshing={isRefreshing}
              />
            </div>
          </div>

          {/* SECTION: Nivel de Servicio */}
          <SectionHeader
            title={t('Nivel de Servicio')}
            description={`NS: ${t('entregas del')} ${selectedDateDisplay}. «${t('Atención mismo día')}»: ${t('recolectados el')} ${selectedDateDisplay}, ${t('entrega el mismo día calendario antes de 21:00.')}`}
          />

          {/* ROW 2: NS por Tipo + Comunas Repartidores (lado a lado) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
            <div>
              <ModernChart
                title={t('NS por Tipo de Paquete')}
                subtitle={`${t('Entregas del')} ${selectedDateDisplay}: % ${t('antes de 21:00 hs. por tipo de paquete')}`}
                height={300}
                loading={initialLoading}
                refreshing={isRefreshing}
              >
                <BarChart data={serviceLevelByTypeMapped} margin={{ top: 8, right: 20, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="name" tick={{ fill: tickColor, fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis domain={[0, 100]} tick={{ fill: tickColor, fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(value, _name, props) => [
                      `${value}% — ${props.payload.before} ${t('de')} ${props.payload.total}`,
                      t('Nivel de servicio'),
                    ]}
                    contentStyle={tooltipStyle}
                    itemStyle={tooltipItemStyle}
                  />
                  <Bar dataKey="pct" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {serviceLevelByTypeMapped.map((entry, index) => (
                      <Cell key={`type-sl-${index}`} fill={slColorByPct(entry.pct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ModernChart>
            </div>
            <div>
              <CommuneRouteTable
                rows={communeUnifiedData.rows}
                loading={initialLoading}
                selectedDateDisplay={selectedDateDisplay}
                tickColor={tickColor}
                refreshing={isRefreshing}
              />
            </div>
          </div>

          {/* SECTION: Operación */}
          <SectionHeader
            title={t('Operación')}
            description={t('Estado actual de paquetes en ruta y recolección del día')}
          />

          {/* ROW 4: Comunas Recolectadas (ancho completo) */}
          <div className="mb-3">
            <div>
              <ModernChart
                title={t('Comunas Recolectadas')}
                subtitle={t('Cantidad de paquetes recolectados hoy agrupados por comuna')}
                height={hBarHeight(collectedByMunicipalityTop.length)}
                loading={initialLoading}
                refreshing={isRefreshing}
              >
                <BarChart
                  data={collectedByMunicipalityTop}
                  layout="vertical"
                  margin={{ top: 4, right: 44, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={true} horizontal={false} />
                  <XAxis type="number" tick={{ fill: tickColor, fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: tickColor, fontSize: 11 }} width={135} />
                  <Tooltip content={<MunicipalityCountTooltip />} />
                  <Bar dataKey="count" fill={COLORS.primary} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                    <LabelList dataKey="count" position="right" style={{ fill: tickColor, fontSize: '11px' }} />
                  </Bar>
                </BarChart>
              </ModernChart>
            </div>
          </div>

          {/* ROW 5: En Ruta por Tipo + Treemap */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-3">
            <div className="lg:col-span-4">
              <ModernChart
                title={t('En Ruta por Tipo de Paquete')}
                subtitle={t('Paquetes con estado En Ruta agrupados por tipo')}
                height={320}
                loading={initialLoading}
                refreshing={isRefreshing}
              >
                <BarChart data={inRouteByTypeMapped} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="name" tick={{ fill: tickColor, fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fill: tickColor, fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => [`${value} ${t('paquetes')}`, t('En ruta')]}
                    contentStyle={tooltipStyle}
                    itemStyle={tooltipItemStyle}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {inRouteByTypeMapped.map((_, index) => (
                      <Cell key={`ir-type-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ModernChart>
            </div>
            <div className="lg:col-span-8">
              <ModernChart
                title={t('Distribución de Paquetes por Cliente')}
                subtitle={t('Volumen recolectado agrupado por empresa remitente')}
                height={320}
                loading={initialLoading}
                refreshing={isRefreshing}
              >
                <Treemap
                  data={treemapData}
                  dataKey="value"
                  nameKey="name"
                  aspectRatio={4 / 3}
                  isAnimationActive={false}
                  content={({ x, y, width, height, index, name }) => (
                    <g>
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        style={{
                          fill: Object.values(COLORS)[index % Object.values(COLORS).length],
                          stroke: 'var(--surface-primary)',
                          strokeWidth: 2,
                        }}
                      />
                      {width > 40 && height > 20 && (
                        <text
                          x={x + width / 2}
                          y={y + height / 2}
                          textAnchor="middle"
                          fill="white"
                          fontSize={Math.min(width / 8, 12)}
                          dominantBaseline="central"
                        >
                          {name}
                        </text>
                      )}
                    </g>
                  )}
                >
                  <Tooltip formatter={(value, name) => [value, name]} />
                </Treemap>
              </ModernChart>
            </div>
          </div>

          {/* ROW 6: Franja horaria (full width) */}
          <div>
            <div>
              <ModernChart
                title={t('Distribución de Entregas por Franja Horaria')}
                subtitle={t('Cantidad de entregas completadas por intervalo de 30 minutos')}
                height={280}
                loading={initialLoading}
                refreshing={isRefreshing}
              >
                <AreaChart data={deliveriesByHalfHourData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorDeliveries" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="time" angle={-45} textAnchor="end" height={55} tick={{ fontSize: 11, fill: tickColor }} />
                  <YAxis domain={[0, maxDeliveries]} tick={{ fontSize: 11, fill: tickColor }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value) => [value, t('Entregas')]}
                    labelFormatter={(label) => `${t('Hora')}: ${label}`}
                    contentStyle={tooltipStyle}
                    itemStyle={tooltipItemStyle}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={COLORS.primary}
                    fillOpacity={1}
                    fill="url(#colorDeliveries)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ModernChart>
            </div>
          </div>
        </TabsContent>

        {/* ── Tab 2: Volumen por Cliente ──────────────────────────────────── */}
        <TabsContent value="2">
          <ClientVolumeTab />
        </TabsContent>

        {/* ── Tab 3: Comparativa Semanal ──────────────────────────────────── */}
        <TabsContent value="3">
          <WeeklyComparisonTab />
        </TabsContent>

        {/* ── Tab 4: Performance Repartidores ────────────────────────────── */}
        <TabsContent value="4">
          <DriverPerformanceTab />
        </TabsContent>

        {/* ── Tab 5: Pendientes por Cliente/Comuna ────────────────────────── */}
        <TabsContent value="5">
          <PendingBreakdownTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// memo: no recibe props; aísla al dashboard de los re-renders del shell padre.
export default memo(ModernDashboardAdmin);
